import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Plus, Minus } from "lucide-react";
import * as Icons from "lucide-react";
import { useState } from "react";
import { Reveal, SectionHeader, CtaLink } from "./primitives";
import { fileUrl } from "@/lib/api";
import { pad2 } from "@/lib/format";

export const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} strokeWidth={1.6} />;
};

export function Hero({ hero }) {
  return (
    <section className="grain relative overflow-hidden border-b hairline pt-32 pb-20 sm:pt-44 sm:pb-28" data-testid="hero-section">
      <div className="glow-violet pointer-events-none absolute -top-48 right-[-15%] h-[680px] w-[680px]" />
      <div className="glow-cyan pointer-events-none absolute bottom-[-30%] left-[-10%] h-[520px] w-[520px]" />
      <div className="container-x relative">
        <p className="eyebrow rise" data-testid="hero-label">{hero.label}</p>
        <h1 className="display-xl rise rise-1 mt-8 max-w-6xl whitespace-pre-line" data-testid="hero-title">{hero.title}</h1>
        <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:items-end">
          <p className="rise rise-2 max-w-2xl text-base text-[var(--text-dim)] md:text-lg lg:col-span-7" data-testid="hero-description">{hero.description}</p>
          <div className="rise rise-3 flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end">
            <CtaLink to="/contact" testId="hero-primary-cta">{hero.primary_cta}</CtaLink>
            <Link to="/work" className="btn-ghost" data-testid="hero-secondary-cta">{hero.secondary_cta} <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
        <div className="rise rise-4 mt-16 inline-flex items-center gap-3 rounded-full border border-[var(--line)] px-4 py-2" data-testid="hero-status">
          <span className="pulse-dot h-2 w-2 rounded-full bg-[var(--accent-2)]" />
          <span className="eyebrow !text-white">{hero.status}</span>
        </div>
      </div>
    </section>
  );
}

export function Stats({ stats }) {
  return (
    <section className="border-b hairline" data-testid="stats-section">
      <div className="container-x grid grid-cols-2 lg:grid-cols-4">
        {(stats.items || []).map((s, i) => (
          <Reveal key={i} delay={i * 0.06} className={`border-[var(--line)] py-10 sm:py-14 ${i % 2 === 1 ? "pl-8 sm:pl-10 lg:border-l" : "pr-8"} ${i >= 2 ? "border-t lg:border-t-0" : ""} ${i > 0 ? "lg:border-l lg:pl-10" : ""}`}>
            <p className="font-display text-3xl font-bold uppercase leading-none text-white sm:text-4xl lg:text-5xl" data-testid={`stat-value-${i}`}>{s.value}</p>
            <p className="eyebrow mt-4">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function WorkCard({ item, index }) {
  return (
    <Link to={`/work/${item.id}`} className="surface card-hover group relative flex flex-col overflow-hidden rounded-lg" data-testid={`work-card-${index}`}>
      <div className="relative aspect-[16/10] overflow-hidden border-b hairline bg-[var(--surface-2)]">
        {item.image ? <img src={fileUrl(item.image)} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /> : (
          <div className="grain relative flex h-full w-full items-end p-6">
            <div className="glow-violet absolute -right-20 -top-20 h-72 w-72" />
            <span className="font-display text-7xl font-extrabold leading-none text-white/10 sm:text-8xl">{pad2(index + 1)}</span>
          </div>
        )}
        <span className="eyebrow absolute left-4 top-4 rounded-full border border-[var(--line-strong)] bg-black/50 px-3 py-1 !text-[10px] text-white backdrop-blur">{item.label}</span>
      </div>
      <div className="flex flex-1 items-start justify-between gap-6 p-6">
        <div>
          <p className="eyebrow"><span className="text-[var(--accent-2)]">{pad2(index + 1)}</span> — {item.category}</p>
          <h3 className="mt-3 font-display text-xl font-bold uppercase text-white sm:text-2xl">{item.title}</h3>
          <p className="mt-3 line-clamp-2 text-sm text-[var(--text-dim)]">{item.description}</p>
        </div>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--line-strong)] transition-[background-color,color] group-hover:bg-[var(--accent)] group-hover:text-[#0b1220]"><ArrowUpRight className="h-4 w-4" /></span>
      </div>
    </Link>
  );
}

export function SelectedWork({ work, full }) {
  const items = full ? work : work.slice(0, 4);
  return (
    <section className="border-b hairline py-20 sm:py-28" data-testid="work-section">
      <div className="container-x">
        {!full && <SectionHeader index="01" label="Selected Work" title={"WORK THAT SHIPS.\nNOT JUST SLIDES."} description="A selection of concept projects and portfolio examples that illustrate how we approach product, engineering and design." action={<CtaLink to="/work" variant="ghost" testId="work-view-all">View all work</CtaLink>} />}
        <div className="grid gap-6 md:grid-cols-2">
          {items.map((w, i) => <Reveal key={w.id} delay={(i % 2) * 0.08}><WorkCard item={w} index={i} /></Reveal>)}
        </div>
        {!items.length && <p className="text-[var(--text-dim)]" data-testid="work-empty">Portfolio items will appear here once published.</p>}
      </div>
    </section>
  );
}

export function Services({ services, full }) {
  return (
    <section className="border-b hairline py-20 sm:py-28" data-testid="services-section">
      <div className="container-x">
        {!full && <SectionHeader index="02" label="Services" title={"EVERYTHING NEEDED\nTO SHIP A PRODUCT."} description="From first prototype to enterprise-grade platform — one team across design, engineering and delivery." action={<CtaLink to="/services" variant="ghost" testId="services-view-all">All services</CtaLink>} />}
        <div className="grid border-l border-t hairline sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={(i % 4) * 0.05} className="group relative border-b border-r hairline p-7 transition-colors duration-300 hover:bg-[var(--surface)]" >
              <div data-testid={`service-card-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="eyebrow text-[var(--accent-2)]">{pad2(i + 1)}</span>
                  <span className="grid h-10 w-10 place-items-center rounded-full border border-[var(--line)] text-white transition-colors group-hover:border-[var(--accent-2)] group-hover:text-[var(--accent-2)]"><Icon name={s.icon} className="h-4.5 w-4.5" /></span>
                </div>
                {s.image && <img src={fileUrl(s.image)} alt="" loading="lazy" className="mt-6 aspect-video w-full rounded object-cover" />}
                <h3 className="mt-10 font-display text-lg font-bold uppercase text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--text-dim)]">{s.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function About({ about }) {
  return (
    <section className="relative overflow-hidden border-b hairline py-20 sm:py-28" data-testid="about-section">
      <div className="glow-violet pointer-events-none absolute left-1/2 top-0 h-[600px] w-[600px] -translate-x-1/2 opacity-60" />
      <div className="container-x relative grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5"><p className="eyebrow"><span className="text-[var(--accent-2)]">03 / </span>About</p><h2 className="display-lg mt-6 whitespace-pre-line">{about.title}</h2></Reveal>
        <Reveal delay={0.1} className="space-y-6 lg:col-span-6 lg:col-start-7">
          {(about.paragraphs || []).map((p, i) => <p key={i} className={`${i === 0 ? "text-lg text-white md:text-xl" : "text-base text-[var(--text-dim)]"} leading-relaxed`}>{p}</p>)}
          <CtaLink to="/about" variant="ghost" testId="about-link">More about us</CtaLink>
        </Reveal>
      </div>
    </section>
  );
}

export function Ceo({ ceo }) {
  return (
    <section className="border-b hairline py-20 sm:py-28" data-testid="ceo-section">
      <div className="container-x grid gap-10 lg:grid-cols-12 lg:items-center">
        <Reveal className="lg:col-span-4">
          <div className="surface relative aspect-[4/5] max-w-sm overflow-hidden rounded-lg">
            {ceo.photo ? <img src={fileUrl(ceo.photo)} alt={ceo.name} className="h-full w-full object-cover" /> : (
              <div className="grain relative flex h-full flex-col justify-between p-6">
                <div className="glow-cyan absolute -bottom-10 -right-10 h-64 w-64" />
                <span className="eyebrow">Leadership</span>
                <span className="font-display text-7xl font-extrabold text-white/10">MR</span>
              </div>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
          <p className="eyebrow"><span className="text-[var(--accent-2)]">04 / </span>CEO</p>
          <blockquote className="mt-8 font-display text-2xl font-semibold leading-tight text-white sm:text-3xl lg:text-4xl whitespace-pre-line" data-testid="ceo-quote">“{ceo.quote}”</blockquote>
          <div className="mt-10 border-t hairline pt-6">
            <p className="font-semibold text-white" data-testid="ceo-name">{ceo.name}</p>
            <p className="mt-1 text-sm text-[var(--text-dim)]">{ceo.position}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Team({ team, members, full }) {
  return (
    <section className="border-b hairline py-20 sm:py-28" data-testid="team-section">
      <div className="container-x">
        {!full && <SectionHeader index="05" label="Team" title={team.title} action={<CtaLink to="/team" variant="ghost" testId="team-view-all">Meet the team</CtaLink>} />}
        <div className="flex flex-wrap gap-3">
          {(team.disciplines || []).map((d, i) => (
            <Reveal key={d} delay={i * 0.04} as="span" className="rounded-full border border-[var(--line-strong)] px-5 py-2.5 text-sm text-white transition-colors hover:border-[var(--accent-2)] hover:text-[var(--accent-2)]">{d}</Reveal>
          ))}
        </div>
        {members?.length > 0 && (
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {members.map((m, i) => (
              <Reveal key={m.id} delay={(i % 4) * 0.06} className="surface card-hover overflow-hidden rounded-lg">
                <div data-testid={`team-member-${i}`}>
                  <div className="aspect-square bg-[var(--surface-2)]">{m.photo ? <img src={fileUrl(m.photo)} alt={m.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center font-display text-5xl font-extrabold text-white/10">{m.name?.slice(0, 1)}</div>}</div>
                  <div className="p-5"><p className="font-semibold text-white">{m.name}</p><p className="mt-1 text-sm text-[var(--text-dim)]">{m.position}</p>{m.bio && <p className="mt-3 text-sm text-[var(--text-dim)] line-clamp-3">{m.bio}</p>}
                    <div className="mt-3 flex gap-3 text-xs">{Object.entries(m.social_links || {}).filter(([, v]) => v).map(([k, v]) => <a key={k} href={v} target="_blank" rel="noreferrer" className="capitalize text-[var(--text-dim)] hover:text-[var(--accent)]">{k}</a>)}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function Process({ process, full }) {
  return (
    <section className="border-b hairline py-20 sm:py-28" data-testid="process-section">
      <div className="container-x">
        {!full && <SectionHeader index="06" label="Process" title={"A CLEAR PATH\nFROM IDEA TO LAUNCH."} action={<CtaLink to="/process" variant="ghost" testId="process-view-all">Our process</CtaLink>} />}
        <ol className="grid gap-px overflow-hidden rounded-lg border hairline bg-[var(--line)] sm:grid-cols-2 lg:grid-cols-3">
          {(process.steps || []).map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06} as="li" className="group bg-[var(--bg)] p-7 transition-colors duration-300 hover:bg-[var(--surface)]">
              <div data-testid={`process-step-${i}`}>
                <span className="font-display text-4xl font-extrabold text-white/15 transition-colors group-hover:text-[var(--accent-2)]">{pad2(i + 1)}</span>
                <h3 className="mt-8 font-display text-lg font-bold uppercase text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--text-dim)]">{s.description}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function Pricing({ pricing }) {
  return (
    <section className="grain relative overflow-hidden border-b hairline py-20 sm:py-28" data-testid="pricing-section">
      <div className="glow-cyan pointer-events-none absolute right-[-10%] top-1/2 h-[600px] w-[600px] -translate-y-1/2" />
      <div className="container-x relative grid gap-10 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-7">
          <p className="eyebrow"><span className="text-[var(--accent-2)]">07 / </span>Pricing</p>
          <h2 className="display-lg mt-6">{pricing.title}</h2>
          <p className="eyebrow mt-12">{pricing.large_text}</p>
          <p className="font-display text-6xl font-extrabold uppercase leading-none text-gradient sm:text-7xl lg:text-8xl" data-testid="pricing-amount">{pricing.amount}</p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9">
          <p className="text-base leading-relaxed text-[var(--text-dim)] md:text-lg">{pricing.description}</p>
          <CtaLink to="/contact" testId="pricing-cta"><span className="mt-0">{pricing.cta}</span></CtaLink>
        </Reveal>
      </div>
    </section>
  );
}

export function Faq({ faqs }) {
  const [open, setOpen] = useState(0);
  return (
    <section className="border-b hairline py-20 sm:py-28" data-testid="faq-section">
      <div className="container-x grid gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-4"><p className="eyebrow"><span className="text-[var(--accent-2)]">08 / </span>FAQ</p><h2 className="display-lg mt-6">COMMON<br />QUESTIONS.</h2></Reveal>
        <div className="lg:col-span-8">
          {faqs.map((f, i) => (
            <div key={f.id} className="border-b hairline">
              <button className="flex w-full items-start justify-between gap-6 py-6 text-left" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} data-testid={`faq-toggle-${i}`}>
                <span className="font-display text-lg font-semibold text-white md:text-xl">{f.question}</span>
                <span className="mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[var(--line-strong)] text-white">{open === i ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}</span>
              </button>
              <div className={`grid transition-[grid-template-rows] duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden"><p className="pb-6 pr-14 text-base leading-relaxed text-[var(--text-dim)]" data-testid={`faq-answer-${i}`}>{f.answer}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ContactCta({ contact }) {
  return (
    <section className="relative overflow-hidden py-24 sm:py-32" data-testid="contact-cta-section">
      <div className="glow-violet pointer-events-none absolute left-[-10%] top-0 h-[600px] w-[600px]" />
      <div className="container-x relative flex flex-col items-start gap-10 lg:flex-row lg:items-end lg:justify-between">
        <div><p className="eyebrow">Contact</p><h2 className="display-xl mt-6 whitespace-pre-line">{contact.title}</h2><p className="mt-6 max-w-xl text-base text-[var(--text-dim)] md:text-lg">{contact.description}</p></div>
        <CtaLink to="/contact" testId="contact-cta-button">Start a Project</CtaLink>
      </div>
    </section>
  );
}
